import { FC } from 'react';
import { Box, Typography } from '@mui/material';
import { FormikProps } from 'formik';

import CustomInput from '../../../../../../../common/CustomInput';
import BlueIOSSwitch from '../../../../../../../pages/private/Profile/components/GeneralSettings/BlueIOSSwitch';
import { CreateActivityFormData } from '../../../../../../../types/activity.types';

import styles from '../../CreateActivityModal.module.scss';
import { useFormFieldRenderer } from '../../hooks/useFormFieldRenderer';

interface OptionalFieldsProps {
  formik: FormikProps<CreateActivityFormData>;
}

const OptionalFields: FC<OptionalFieldsProps> = ({ formik }) => {
  const { values, handleChange, setFieldValue } = formik;
  const activityType = values.activityType;
  const { isFieldVisible, isFieldMandatory, getError, renderFieldInput, renderAmountSpentInput } =
    useFormFieldRenderer(formik, activityType);

  const handleToggleChange = (fieldName: keyof CreateActivityFormData, dependentFields: string[]) => {
    return (event: React.ChangeEvent<HTMLInputElement>) => {
      const isChecked = event.target.checked;
      setFieldValue(fieldName, isChecked);

      if (!isChecked) {
        dependentFields.forEach(field => {
          setFieldValue(field, '');
          setFieldValue(`${field}_details`, null);
        });
      }
    };
  };

  return (
    <Box className={styles.formSection}>
      {activityType === 'Flight' && (
        <>
          {renderFieldInput('airline', 'Airline')}
          {renderFieldInput('flightNumber', 'Flight Number')}
          <Box className={styles.locationFields}>
            {renderFieldInput('ticketBookingNumber', 'Ticket/Booking #')}
            {renderFieldInput('seatNumber', 'Seat #')}
          </Box>
          <CustomInput
            inputName="notes"
            inputLabel="Notes"
            value={values.notes || ''}
            onChange={handleChange}
            error={getError('notes')}
          />
          {renderAmountSpentInput()}
        </>
      )}

      {isFieldVisible('airline') && activityType !== 'Flight' && renderFieldInput('airline', 'Airline')}

      {isFieldVisible('flightNumber') && activityType !== 'Flight' && renderFieldInput('flightNumber', 'Flight Number')}

      {isFieldVisible('carrierName') && renderFieldInput('carrierName', 'Carrier Name')}

      {isFieldVisible('bookingNumber') && activityType !== 'Rail' && renderFieldInput('bookingNumber', 'Booking #')}

      {isFieldVisible('ticketBookingNumber') && activityType !== 'Flight' &&
        renderFieldInput('ticketBookingNumber', 'Ticket/Booking #')}

      {isFieldVisible('seatNumber') && activityType !== 'Rail' && activityType !== 'Flight' &&
        renderFieldInput('seatNumber', 'Seat #')}

      {isFieldVisible('address') && renderFieldInput('address', 'Address')}

      {isFieldVisible('pickupAddress') && renderFieldInput('pickupAddress', 'Pickup Address')}

      {isFieldVisible('sameAsPickupAddress') && (
        <Box className={styles.dropOffAddressSection}>
          <Typography
            variant="h11Semibold"
            className={styles.dropOffAddressLabel}
          >
            Drop Off Address
          </Typography>
          <Box className={styles.toggleRow}>
            <Typography
              variant="h11"
              className={styles.toggleRowLabel}
            >
              Same as Pickup Address
            </Typography>
            <BlueIOSSwitch
              checked={values.sameAsPickupAddress}
              onChange={handleToggleChange('sameAsPickupAddress', ['dropOffAddress'])}
              name="sameAsPickupAddress"
            />
          </Box>
          {!values.sameAsPickupAddress && isFieldVisible('dropOffAddress') && (
            renderFieldInput('dropOffAddress', 'Drop Off Address')
          )}
        </Box>
      )}

      {isFieldVisible('officeAddress') && renderFieldInput('officeAddress', 'Office Address')}

      {isFieldVisible('place') && !isFieldMandatory('place') && renderFieldInput('place', 'Place')}

      {isFieldVisible('vehicleType') && !isFieldMandatory('vehicleType') &&
        renderFieldInput('vehicleType', 'Vehicle Type / Description')}

      {isFieldVisible('vehicleDescription') && renderFieldInput('vehicleDescription', 'Vehicle Description')}
      {isFieldVisible('shipName') && renderFieldInput('shipName', 'Ship Name')}

      {isFieldVisible('startingPortName') && (
        <>
          <CustomInput
            inputName="startingPortName"
            inputLabel="Starting Port Name"
            value={values.startingPortName || ''}
            onChange={handleChange}
            error={getError('startingPortName')}
          />
          {renderFieldInput('startingPortAddress', 'Starting Port Address')}
        </>
      )}

      {isFieldVisible('sameAsStartingPort') && (
        <Box className={styles.dropOffAddressSection}>
          <Typography
            variant="h11Semibold"
            className={styles.dropOffAddressLabel}
          >
            Ending Port
          </Typography>
          <Box className={styles.toggleRow}>
            <Typography
              variant="h11"
              className={styles.toggleRowLabel}
            >
              Same as Starting Port
            </Typography>
            <BlueIOSSwitch
              checked={values.sameAsStartingPort}
              onChange={handleToggleChange('sameAsStartingPort', ['endingPortName', 'endingPortAddress'])}
              name="sameAsStartingPort"
            />
          </Box>
          {!values.sameAsStartingPort && isFieldVisible('endingPortName') && (
            <>
              <CustomInput
                inputName="endingPortName"
                inputLabel="Ending Port Name"
                value={values.endingPortName || ''}
                onChange={handleChange}
                error={getError('endingPortName')}
              />
              {renderFieldInput('endingPortAddress', 'Ending Port Address')}
            </>
          )}
        </Box>
      )}
      {activityType === 'Rail' && (
        <>
          {renderFieldInput('bookingNumber', 'Booking #')}
          <CustomInput
            inputName="departureStation"
            inputLabel="Departure Station"
            value={values.departureStation || ''}
            onChange={handleChange}
            error={getError('departureStation')}
          />
          {renderFieldInput('departureAddress', 'Departure Address')}
          <CustomInput
            inputName="arrivalStation"
            inputLabel="Arrival Station"
            value={values.arrivalStation || ''}
            onChange={handleChange}
            error={getError('arrivalStation')}
          />
          {renderFieldInput('arrivalAddress', 'Arrival Address')}
          <Box className={styles.locationFields}>
            <CustomInput
              inputName="trainType"
              inputLabel="Train Type"
              value={values.trainType || ''}
              onChange={handleChange}
              error={getError('trainType')}
            />
            <CustomInput
              inputName="trainNumber"
              inputLabel="Train #"
              value={values.trainNumber || ''}
              onChange={handleChange}
              error={getError('trainNumber')}
            />
          </Box>
          <Box className={styles.locationFields}>
            <CustomInput
              inputName="class"
              inputLabel="Class"
              value={values.class || ''}
              onChange={handleChange}
              error={getError('class')}
              placeholder="Enter class (e.g., Economy, Business, First Class)"
            />
            {renderFieldInput('seatNumber', 'Seat #')}
          </Box>
          <CustomInput
            inputName="notes"
            inputLabel="Notes"
            value={values.notes || ''}
            onChange={handleChange}
            error={getError('notes')}
          />
          {renderAmountSpentInput()}
        </>
      )}

      {isFieldVisible('departureStation') && activityType !== 'Rail' && (
        <Box className={styles.locationFields}>
          <CustomInput
            inputName="departureStation"
            inputLabel="Departure Station"
            value={values.departureStation || ''}
            onChange={handleChange}
            error={getError('departureStation')}
          />
          <CustomInput
            inputName="arrivalStation"
            inputLabel="Arrival Station"
            value={values.arrivalStation || ''}
            onChange={handleChange}
            error={getError('arrivalStation')}
          />
        </Box>
      )}

      {isFieldVisible('departureLocation') && (
        <>
          <CustomInput
            inputName="departureLocation"
            inputLabel="Departure Location"
            value={values.departureLocation || ''}
            onChange={handleChange}
            error={getError('departureLocation')}
          />
          {isFieldVisible('departureAddress') && renderFieldInput('departureAddress', 'Departure Address')}
          <CustomInput
            inputName="arrivalLocation"
            inputLabel="Arrival Location"
            value={values.arrivalLocation || ''}
            onChange={handleChange}
            error={getError('arrivalLocation')}
          />
          {isFieldVisible('arrivalAddress') && renderFieldInput('arrivalAddress', 'Arrival Address')}
        </>
      )}

      {isFieldVisible('departureAddress') && !isFieldVisible('departureLocation') && activityType !== 'Rail' &&
        renderFieldInput('departureAddress', 'Departure Address')}
      {isFieldVisible('arrivalAddress') && !isFieldVisible('arrivalLocation') && activityType !== 'Rail' &&
        renderFieldInput('arrivalAddress', 'Arrival Address')}

      {isFieldVisible('trainType') && activityType !== 'Rail' && (
        <Box className={styles.locationFields}>
          <CustomInput
            inputName="trainType"
            inputLabel="Train Type"
            value={values.trainType || ''}
            onChange={handleChange}
            error={getError('trainType')}
          />
          <CustomInput
            inputName="trainNumber"
            inputLabel="Train #"
            value={values.trainNumber || ''}
            onChange={handleChange}
            error={getError('trainNumber')}
          />
        </Box>
      )}
      {isFieldVisible('class') && activityType !== 'Rail' && (
        <CustomInput
          inputName="class"
          inputLabel="Class"
          value={values.class || ''}
          onChange={handleChange}
          error={getError('class')}
          placeholder="Enter class (e.g., Economy, Business, First Class)"
        />
      )}
      {isFieldVisible('borderCheckpoint') && renderFieldInput('borderCheckpoint', 'Border Checkpoint')}

      {isFieldVisible('parkingSpaceNumber') && renderFieldInput('parkingSpaceNumber', 'Parking Space #')}

      {isFieldVisible('numberOfPassengers') && (
        <CustomInput
          inputName="numberOfPassengers"
          inputLabel="Number of Passengers"
          value={
            values.numberOfPassengers !== undefined && values.numberOfPassengers !== null
              ? String(values.numberOfPassengers)
              : ''
          }
          onChange={handleChange}
          error={getError('numberOfPassengers')}
        />
      )}

      {isFieldVisible('cabinType') && (
        <Box className={styles.locationFields}>
          <CustomInput
            inputName="cabinType"
            inputLabel="Cabin Type"
            value={values.cabinType || ''}
            onChange={handleChange}
            error={getError('cabinType')}
          />
          <CustomInput
            inputName="cabinNumber"
            inputLabel="Cabin #"
            value={values.cabinNumber || ''}
            onChange={handleChange}
            error={getError('cabinNumber')}
          />
        </Box>
      )}
      {isFieldVisible('notes') && activityType !== 'Rail' && activityType !== 'Flight' && (
        <CustomInput
          inputName="notes"
          inputLabel="Notes"
          value={values.notes || ''}
          onChange={handleChange}
          error={getError('notes')}
        />
      )}

      {isFieldVisible('amountSpent') && activityType !== 'Rail' && activityType !== 'Flight' && renderAmountSpentInput()}
    </Box>
  );
};

export default OptionalFields;
